import React from 'react';
import { Link } from 'react-router-dom';
import './CookiePolicy.css';

function CookiePolicy() {
  return (
    <div className="cookie-page">
      <div className="cookie-hero">
        <div className="container">
          <h1 className="cookie-title">Cookie Policy</h1>
          <p className="cookie-date">Last Updated: December 7, 2025</p>
        </div>
      </div>

      <div className="cookie-content">
        <div className="container">
          <section className="cookie-section">
            <h2>What Are Cookies</h2>
            <p>
              Cookies are small text files that are placed on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and 
              understanding how you use our site.
            </p>
          </section>

          <section className="cookie-section">
            <h2>How We Use Cookies</h2>
            <p>
              CreateMyApp uses cookies for the following purposes:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
              <li><strong>Performance Cookies:</strong> Monitor and improve website performance</li>
            </ul>
          </section>

          <section className="cookie-section">
            <h2>Types of Cookies We Use</h2>
            
            <div className="cookie-type">
              <h3>1. Strictly Necessary Cookies</h3>
              <p>
                These cookies are essential for the website to function and cannot be switched off. 
                They are usually set in response to actions you take, such as:
              </p>
              <ul>
                <li>Submitting forms</li>
                <li>Setting privacy preferences</li>
                <li>Logging in to secure areas</li>
              </ul>
              <p><strong>Duration:</strong> Session or up to 1 year</p>
            </div>

            <div className="cookie-type">
              <h3>2. Functional Cookies</h3>
              <p>
                These cookies enable enhanced functionality and personalization:
              </p>
              <ul>
                <li>Remembering your preferences</li>
                <li>Saving form data to prevent re-submission</li>
                <li>Maintaining your session state</li>
              </ul>
              <p><strong>Duration:</strong> Up to 1 year</p>
              <p><strong>Example:</strong> localStorage for form submission tracking</p>
            </div>

            <div className="cookie-type">
              <h3>3. Analytics Cookies</h3>
              <p>
                These cookies help us understand how visitors interact with our website by collecting 
                and reporting information anonymously:
              </p>
              <ul>
                <li>Number of visitors</li>
                <li>Pages visited</li>
                <li>Time spent on pages</li>
                <li>Traffic sources</li>
              </ul>
              <p><strong>Duration:</strong> Up to 2 years</p>
              <p><strong>Third-party services:</strong> We may use Google Analytics or similar services</p>
            </div>

            <div className="cookie-type">
              <h3>4. Performance Cookies</h3>
              <p>
                These cookies help us monitor and improve website performance:
              </p>
              <ul>
                <li>Page load times</li>
                <li>Error tracking</li>
                <li>User experience metrics</li>
              </ul>
              <p><strong>Duration:</strong> Session or up to 1 year</p>
            </div>
          </section>

          <section className="cookie-section">
            <h2>Third-Party Cookies</h2>
            <p>
              We may use third-party services that set their own cookies. These include:
            </p>
            <ul>
              <li><strong>Google Fonts:</strong> For displaying web fonts</li>
              <li><strong>Google Analytics:</strong> For website analytics (if enabled)</li>
              <li><strong>Social Media:</strong> If you interact with social sharing features</li>
            </ul>
            <p>
              These third parties have their own privacy policies, and we have no control over their cookies.
            </p>
          </section>

          <section className="cookie-section">
            <h2>Cookies We Set</h2>
            <div className="cookie-table">
              <table>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>lastFormSubmit</td>
                    <td>Prevents spam by tracking form submission timing</td>
                    <td>Session</td>
                  </tr>
                  <tr>
                    <td>userPreferences</td>
                    <td>Stores user preferences and settings</td>
                    <td>1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="cookie-section">
            <h2>Managing Cookies</h2>
            <p>
              You have the right to decide whether to accept or reject cookies. You can manage your 
              cookie preferences in several ways:
            </p>
            
            <h3>Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul>
              <li>Block all cookies</li>
              <li>Block third-party cookies</li>
              <li>Delete cookies after each session</li>
              <li>Set exceptions for specific websites</li>
            </ul>

            <h3>Browser-Specific Instructions</h3>
            <ul>
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
            </ul>

            <p className="warning-note">
              ⚠️ <strong>Note:</strong> Blocking or deleting cookies may affect the functionality of our website 
              and prevent you from using certain features, such as the contact form.
            </p>
          </section>

          <section className="cookie-section">
            <h2>Do Not Track</h2>
            <p>
              Some browsers have a "Do Not Track" feature that signals websites you visit that you do not 
              want your online activity tracked. Currently, our website does not respond to Do Not Track signals, 
              but we respect your privacy choices and collect minimal data.
            </p>
          </section>

          <section className="cookie-section">
            <h2>Mobile Devices</h2>
            <p>
              Mobile devices use identifiers similar to cookies. You can manage these through your device settings:
            </p>
            <ul>
              <li><strong>iOS:</strong> Settings → Safari → Block All Cookies</li>
              <li><strong>Android:</strong> Settings → Privacy → Clear browsing data</li>
            </ul>
          </section>

          <section className="cookie-section">
            <h2>Changes to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in technology, 
              legislation, or our business practices. We will notify you of any significant changes 
              by posting the new policy on this page with an updated revision date.
            </p>
          </section>

          <section className="cookie-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about our use of cookies or this Cookie Policy, please contact us at:
            </p>
            <p className="contact-info">
              📧 <a href="mailto:info@createmyapp.ca">info@createmyapp.ca</a><br />
              📱 604-773-1594<br />
              📍 Surrey, BC, Canada
            </p>
          </section>

          <div className="back-button-container">
            <Link to="/" className="back-button">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
